import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../api';

// IT-opgaver: the findings a customer has collected to send to its IT partner, each with the objects that were picked inside it.
// One list per customer, kept on the server; every component that shows it (the cards in Gennemgang, the badge in the menu, the
// list in Beslutninger) reads the same copy here.
const lists = new Map(); // tenantId -> items
const listeners = new Set();
const inflight = new Map();
const notify = () => listeners.forEach((fn) => fn());

async function request(path, options) {
  const res = await apiFetch(path, options);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Kunne ikke opdatere IT-opgaver');
  return data;
}
const post = (path, body) => request(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

function load(tenantId) {
  if (!tenantId || inflight.has(tenantId)) return;
  const p = request(`/api/it-list?tenantId=${encodeURIComponent(tenantId)}`)
    .then((d) => { lists.set(tenantId, d.items || []); notify(); })
    .catch(() => { if (!lists.has(tenantId)) { lists.set(tenantId, []); notify(); } })
    .finally(() => inflight.delete(tenantId));
  inflight.set(tenantId, p);
}

export function useItList(tenantId) {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((n) => n + 1);
    listeners.add(fn);
    if (tenantId && !lists.has(tenantId)) load(tenantId);
    return () => { listeners.delete(fn); };
  }, [tenantId]);

  const items = (tenantId && lists.get(tenantId)) || [];
  const apply = (data) => { lists.set(tenantId, data.items || []); notify(); };
  // item: { key, title, area, tone, objects: [{ id, name }] }. Objects are added to what is already there.
  const add = useCallback(async (item) => apply(await post('/api/it-list', { tenantId, ...item })), [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Without objectIds the whole item goes; with them only those objects (and the item when none is left).
  const remove = useCallback(async (key, objectIds) => apply(await post('/api/it-list/remove', { tenantId, key, objectIds })), [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps
  const markSent = useCallback(async (keys) => apply(await post('/api/it-list/sent', { tenantId, keys })), [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps
  // The mail was not sent after all: the items go back to waiting.
  const unmarkSent = useCallback(async (keys) => apply(await post('/api/it-list/sent', { tenantId, keys, undo: true })), [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Is this finding (or this one object in it) waiting on the list? An item without objects stands for the whole finding.
  const has = (key, objectId) => {
    const item = items.find((i) => i.key === key && !i.sentAt);
    if (!item) return false;
    if (objectId == null || !(item.objects || []).length) return true;
    return item.objects.some((o) => o.id === objectId);
  };
  return {
    items,
    ready: !!tenantId && lists.has(tenantId),
    waiting: items.filter((i) => !i.sentAt),
    sent: items.filter((i) => i.sentAt),
    has,
    add,
    remove,
    markSent,
    unmarkSent,
  };
}
